import { useCallback, useEffect, useMemo, useState } from 'react'

import { REACT_APP_READER_URL, REACT_APP_APPLE_CLIENT_ID, REACT_APP_APPLE_REDIRECT_URI } from 'env'
import { useQueryParams } from 'hooks/useQueryParams'
import { Facebook, Google } from 'blaze-ui/icons'
import { AppleIcon } from 'components/AppleIcon/AppleIcon'
import Styles from './OauthLoginButton.module.scss'
import { Button } from 'blaze-ui/Button'
import { navigateToExternalUrl } from 'utils/location'
import { isWebview } from 'utils/browserUtils'
import { RegistrationType } from 'screens/LoginOrSignup/utils'
import { IS_VM_REGISTRATION_KEY } from 'flows/common/steps/Registration/constants'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

type Props = {
  workspaceInviteToken: string
  provider: RegistrationType.Google | RegistrationType.Facebook | RegistrationType.Apple
}

const OauthLoginButton = ({ workspaceInviteToken, provider }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isAppleSdkReady, setIsAppleSdkReady] = useState(false)
  const { domain, redirect_to, promotion_code } = useQueryParams()

  const providerConfig = useMemo(
    () => ({
      google: {
        authUrlPath: 'google_oauth2',
        buttonText: 'Continue with Google',
        icon: <Google size={24} />,
        logoClass: Styles.googleLogo,
        dataHeapId: 'continue-with-google',
      },
      facebook: {
        authUrlPath: 'facebook',
        buttonText: 'Continue with Facebook',
        icon: <Facebook size={24} />,
        logoClass: Styles.facebookLogo,
        dataHeapId: 'continue-with-facebook',
      },
      apple: {
        authUrlPath: 'apple',
        buttonText: 'Continue with Apple',
        icon: <AppleIcon size={24} />,
        logoClass: Styles.appleLogo,
        dataHeapId: 'continue-with-apple',
      },
    }),
    [],
  )

  useEffect(() => {
    if (provider !== RegistrationType.Apple) return

    setIsAppleSdkReady(false)

    if (!REACT_APP_APPLE_CLIENT_ID || !REACT_APP_APPLE_REDIRECT_URI) return

    const initAppleSDK = () => {
      if (window.AppleID?.auth?.init) {
        window.AppleID.auth.init({
          clientId: REACT_APP_APPLE_CLIENT_ID,
          redirectURI: REACT_APP_APPLE_REDIRECT_URI,
          scope: 'name email',
          usePopup: false,
        })
        setIsAppleSdkReady(true)
      } else {
        setTimeout(initAppleSDK, 500)
      }
    }

    initAppleSDK()
  }, [provider])

  const loginOrSignup = useCallback(async () => {
    localStorage.setItem(IS_VM_REGISTRATION_KEY, 'true')

    setIsProcessing(true)

    if (provider === RegistrationType.Apple && isAppleSdkReady && window.AppleID?.auth?.signIn) {
      try {
        await window.AppleID.auth.signIn()
      } catch (error) {
        setIsProcessing(false)
        return
      }
    }

    const state = {
      redirect_to,
      workspace_invite_token: workspaceInviteToken,
      domain,
      promotion_code,
    }

    const encodedState = btoa(JSON.stringify(state))
    const authUrl = `${REACT_APP_READER_URL}/omniauth/${providerConfig[provider].authUrlPath}?state=${encodedState}`

    if (window.top && window.top !== window) {
      window.top.location.href = authUrl
      return
    }

    navigateToExternalUrl(authUrl)
  }, [domain, redirect_to, workspaceInviteToken, promotion_code, provider, isAppleSdkReady, providerConfig])

  if (
    isWebview(window.navigator.userAgent) &&
    (provider === RegistrationType.Google || provider === RegistrationType.Apple)
  )
    return null

  const size: 'md' | 'xl' = isMobile ? 'md' : 'xl'

  const button = (
    <Button
      variant="secondary"
      size={size}
      fullWidth
      useChildClass
      onPress={loginOrSignup}
      isDisabled={isProcessing}
      data-heap-id={providerConfig[provider].dataHeapId}
      className={provider === RegistrationType.Apple ? Styles.appleButton : undefined}
      frontIcon={() => providerConfig[provider].icon}
    >
      <span className={Styles.buttonText}>{providerConfig[provider].buttonText}</span>
    </Button>
  )

  if (provider === RegistrationType.Apple) {
    return <div className={Styles.appleButtonWrapper}>{button}</div>
  }

  return button
}

export default OauthLoginButton
